'use client';

import { useRef, type ReactNode } from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * Confirmation of an irreversible account action („Zrušiť konto“, leaving the newsletter) – the
 * design has no dialog, so it is the core dialog in the card style of the site: white, radius 16,
 * padding 24, the H5 title over a Body/L text, the action and „Zavrieť“. The safe „Zavrieť“ takes
 * the focus; while `busy` the dialog cannot be dismissed.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirm,
  error,
  busy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** The confirming button. */
  confirm: ReactNode;
  error?: string | null;
  busy?: boolean;
}) {
  const { t } = useTranslations();
  const closeRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={next => (busy ? undefined : onOpenChange(next))}>
      <DialogContent
        className="max-w-[calc(100vw-32px)] gap-4 rounded-2xl border-[#E2E5DF] bg-white p-6 shadow-raised sm:max-w-[480px]"
        onOpenAutoFocus={event => {
          event.preventDefault();
          closeRef.current?.focus();
        }}
      >
        <DialogTitle className="nt-h5 pr-6 text-[#0D110D]">{title}</DialogTitle>
        <DialogDescription className="nt-body-l text-[#363D36]">{description}</DialogDescription>
        {error ? (
          <p role="alert" className="nt-body-m text-[#B42318]">
            {error}
          </p>
        ) : null}
        <div className="flex flex-col gap-3 pt-2 sm:flex-row">
          {confirm}
          <DialogClose asChild>
            <Button ref={closeRef} type="button" variant="outline" disabled={busy}>
              {t('text272')}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
